import type { FC, ReactNode } from 'react';

import { DateTime } from 'luxon';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

import type { NewsData } from './type';
import { cn } from '@wwe-portal/ui/lib/lib';
import { formatDateTime } from '@wwe-portal/utils/date';
import { AspectRatio } from '@wwe-portal/ui/components/aspect-ratio';

export interface NewsCardProps {
  data: NewsData;
  cover: ReactNode;
  category: ReactNode;
  readMore: ReactNode;
  hideBg?: boolean;
  title?: ReactNode;
  className?: string;
  contentClassName?: string;
}

export const NewsCardCategory: FC<{ data: Pick<NewsData, 'category'> }> = ({
  data,
}) => {
  return (
    <div className="border-back200 bg-back800 text-text500 hover:text-green800 rounded-md border border-solid px-3.5 py-1.5 transition duration-300 ease-linear">
      #{data.category}
    </div>
  );
};

export const NewsCardReadmore = () => {
  return (
    <div className="text-text500 hover:text-green800 flex items-center gap-2 transition duration-300 ease-linear hover:no-underline">
      <span>Read More </span>
      <IcoMoonIcon className="size-5" iconName="arrow-right" />
    </div>
  );
};

export const NewsCard: FC<NewsCardProps> = ({
  data,
  cover,
  category,
  title,
  readMore,
}) => {
  return (
    <div className="bg-back600 hidden overflow-hidden rounded-lg md:block">
      <AspectRatio ratio={768 / 402} className="relative">
        {cover}
      </AspectRatio>
      <div className="px-5 py-4">
        <div className="text-text500 mb-1 text-xs">
          {DateTime.fromISO(data.pubDate).toFormat('MMM dd, yyyy')}
        </div>
        <div className="text-text300 mb-4 truncate text-base font-medium">
          {title}
        </div>
        <div className="text-text500 mb-8 line-clamp-2 text-xs">
          {data.contentPreview}
        </div>
        <div className="flex items-center justify-between text-xs">
          {category}
          {readMore}
        </div>
      </div>
    </div>
  );
};

export const NewsCardMobile: FC<NewsCardProps> = ({
  data,
  cover,
  hideBg = false,
  className,
  contentClassName,
  title,
  category,
}) => {
  return (
    <div
      className={cn(
        'bg-back400 flex justify-between rounded-lg p-2.5 md:hidden',
        className
      )}
    >
      <div className="text-xs font-medium">
        <div>{title}</div>
        <div
          className={cn(
            'text-text500 flex items-center gap-2.5',
            contentClassName
          )}
        >
          <span>{formatDateTime(data.pubDate, 'DD')}</span>
          <span className="bg-text500 size-1 rounded-full" />
          {category}
        </div>
      </div>
      {!hideBg && cover}
    </div>
  );
};
